/**
 * ResearchHubPage - Historical scholarship and research area
 * 
 * Contains the archive's historical studies, evidence-led articles, and topic pages:
 * - Ticket production history
 * - Design and printing lineage
 * - Concert chronology
 * - Charity and fundraising history
 * - Broadcast history
 * - Venue and arena research
 * - Personnel and organizational history
 * - Graceland exhibition history
 * - Artifact provenance studies
 * - Evidence matrices
 * - Open research questions
 */

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Search, BookOpen, FileText, HelpCircle, ChevronRight, Calendar, User } from 'lucide-react';
import '../../styles/research-hub.css';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Research Topics
const RESEARCH_TOPICS = [
  {
    slug: 'ticket-production',
    title: 'Ticket Production History',
    description: 'The complete story of how the Aloha from Hawaii tickets were designed, printed, and distributed.',
    icon: '🎫',
    articleCount: 3
  },
  {
    slug: 'design-lineage',
    title: 'Design & Printing Lineage',
    description: 'Globe Tickets of Los Angeles and the design evolution of the admission materials.',
    icon: '🎨',
    articleCount: 2
  },
  {
    slug: 'concert-chronology',
    title: 'Concert Chronology',
    description: 'A detailed timeline of the January 14, 1973 concert and satellite broadcast.',
    icon: '📅',
    articleCount: 4
  },
  {
    slug: 'charity-fundraising',
    title: 'Charity & Fundraising',
    description: 'The Kui Lee Cancer Fund and the charitable purpose behind the concert.',
    icon: '❤️',
    articleCount: 2
  },
  {
    slug: 'broadcast-history',
    title: 'Broadcast History',
    description: 'The satellite technology and global broadcast that made history.',
    icon: '📡',
    articleCount: 5
  },
  {
    slug: 'venue-research',
    title: 'Venue & Arena Research',
    description: 'The Honolulu International Center Arena: seating, capacity, and configuration.',
    icon: '🏟️',
    articleCount: 3
  },
  {
    slug: 'graceland-exhibition',
    title: 'Graceland Exhibition History',
    description: 'The curation and display of Aloha materials at Graceland through the years.',
    icon: '🏛️',
    articleCount: 2
  },
  {
    slug: 'provenance-studies',
    title: 'Artifact Provenance Studies',
    description: 'Tracing the ownership and history of significant archive items.',
    icon: '🔍',
    articleCount: 4
  }
];

// Featured Studies
const FEATURED_STUDIES = [
  {
    slug: 'globe-tickets-story',
    title: 'The Globe Tickets Story',
    subtitle: 'How Los Angeles\'s Premier Ticket Printer Created History',
    date: '2024-01-15',
    author: 'Archive Research Team',
    readTime: '12 min read',
    featured: true
  },
  {
    slug: 'satellite-broadcast-technology',
    title: 'The Technology Behind the Broadcast',
    subtitle: 'Understanding the 1973 Satellite Infrastructure',
    date: '2024-02-20',
    author: 'Archive Research Team',
    readTime: '8 min read'
  },
  {
    slug: 'seating-chart-analysis',
    title: 'HIC Arena Seating Analysis',
    subtitle: 'Reconstructing the Concert Configuration',
    date: '2024-03-10',
    author: 'Archive Research Team',
    readTime: '15 min read'
  }
];

// Open Research Questions
const OPEN_QUESTIONS = [
  {
    id: 1,
    question: 'What was the exact distribution method for complimentary tickets?',
    status: 'investigating',
    responses: 3
  },
  {
    id: 2,
    question: 'How many tickets were printed but never distributed?',
    status: 'open',
    responses: 0
  },
  {
    id: 3,
    question: 'What happened to the original printing plates?',
    status: 'investigating',
    responses: 2
  }
];

const ResearchHubPage = ({ view }) => {
  const { topicSlug, studySlug } = useParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('topics');
  
  // Determine view based on route params
  const currentView = view || (topicSlug ? 'topic' : studySlug ? 'study' : 'index');
  
  // Get current topic
  const currentTopic = topicSlug 
    ? RESEARCH_TOPICS.find(t => t.slug === topicSlug)
    : null;
  
  return (
    <div className="research-hub">
      <Helmet>
        <title>{`${currentTopic ? `${currentTopic.title} | Research` : 'Research & Scholarship'} | Elvis Aloha Collection`}</title>
        <meta name="description" content={currentTopic ? currentTopic.description : 'Evidence-based historical research into the Aloha from Hawaii concert and broadcast.'} />
        <link rel="canonical" href={window.location.href} />
      </Helmet>
      {/* Hero Header */}
      <header className="research-header">
        <div className="research-header__inner">
          {currentTopic ? (
            <>
              <Link to="/research" className="research-header__back">
                <ChevronRight size={16} style={{ transform: 'rotate(180deg)' }} />
                All Research
              </Link>
              <span className="research-header__icon">{currentTopic.icon}</span>
              <h1 className="research-header__title">{currentTopic.title}</h1>
              <p className="research-header__description">{currentTopic.description}</p>
            </>
          ) : (
            <>
              <h1 className="research-header__title">Research & Scholarship</h1>
              <p className="research-header__subtitle">
                Evidence-based historical research into the Aloha from Hawaii concert and broadcast
              </p>
            </>
          )}
        </div>
      </header>
      
      {/* Navigation Tabs */}
      {!currentTopic && (
        <nav className="research-tabs">
          <button 
            className={activeTab === 'topics' ? 'active' : ''}
            onClick={() => setActiveTab('topics')}
          >
            <BookOpen size={18} />
            Topics
          </button>
          <button 
            className={activeTab === 'studies' ? 'active' : ''}
            onClick={() => setActiveTab('studies')}
          >
            <FileText size={18} />
            Studies
          </button>
          <button 
            className={activeTab === 'questions' ? 'active' : ''}
            onClick={() => setActiveTab('questions')}
          >
            <HelpCircle size={18} />
            Open Questions
          </button>
        </nav>
      )}
      
      {/* Search Bar */}
      <div className="research-search">
        <Search size={18} />
        <input
          type="text"
          placeholder="Search research materials..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>
      
      {/* Main Content */}
      <div className="research-content">
        {/* Topics Grid */}
        {activeTab === 'topics' && !currentTopic && (
          <section className="research-topics">
            <h2>Research Topics</h2>
            <div className="topics-grid">
              {RESEARCH_TOPICS.map(topic => (
                <Link 
                  key={topic.slug}
                  to={`/research/topics/${topic.slug}`}
                  className="topic-card"
                >
                  <span className="topic-card__icon">{topic.icon}</span>
                  <h3 className="topic-card__title">{topic.title}</h3>
                  <p className="topic-card__description">{topic.description}</p>
                  <span className="topic-card__count">{topic.articleCount} articles</span>
                </Link>
              ))}
            </div>
          </section>
        )}
        
        {/* Studies List */}
        {activeTab === 'studies' && !currentTopic && (
          <section className="research-studies">
            <h2>Published Studies</h2>
            <div className="studies-list">
              {FEATURED_STUDIES.map(study => (
                <Link 
                  key={study.slug}
                  to={`/research/studies/${study.slug}`}
                  className={`study-card ${study.featured ? 'study-card--featured' : ''}`}
                >
                  <div className="study-card__content">
                    <h3 className="study-card__title">{study.title}</h3>
                    <p className="study-card__subtitle">{study.subtitle}</p>
                    <div className="study-card__meta">
                      <span><Calendar size={14} /> {study.date}</span>
                      <span><User size={14} /> {study.author}</span>
                      <span>{study.readTime}</span>
                    </div>
                  </div>
                  <ChevronRight className="study-card__arrow" size={20} />
                </Link>
              ))}
            </div>
          </section>
        )}
        
        {/* Open Questions */}
        {activeTab === 'questions' && !currentTopic && (
          <section className="research-questions">
            <h2>Open Research Questions</h2>
            <p className="questions-intro">
              These are active areas of investigation where the archive is seeking additional information or documentation.
            </p>
            <div className="questions-list">
              {OPEN_QUESTIONS.map(q => (
                <div key={q.id} className="question-card">
                  <span className={`question-status question-status--${q.status}`}>
                    {q.status === 'investigating' ? 'Investigating' : 'Open'}
                  </span>
                  <h3 className="question-card__text">{q.question}</h3>
                  {q.responses > 0 && (
                    <span className="question-card__responses">
                      {q.responses} response{q.responses !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}
        
        {/* Topic Detail View */}
        {currentTopic && (
          <section className="topic-detail">
            <p className="topic-detail__placeholder">
              Research articles for "{currentTopic.title}" will be displayed here.
            </p>
          </section>
        )}
      </div>
    </div>
  );
};

export default ResearchHubPage;
